'use client';

import { createListCollection, Flex, Input, SimpleGrid, Text, Textarea } from '@chakra-ui/react';
import { memo, useMemo } from 'react';

import EntityPicker from '@components/common/EntityPicker';
import { InputGroup } from '@components/ui/input-group';
import { SUPPLIER_PAYOUT_NETWORK_LABEL } from '@features/finance/financeMeta';
import SelectField from '@features/spend/SelectField';
import { allSuppliers } from 'helpers/fakeData';

const supplierOptions = allSuppliers.map((supplier) => ({
  id: supplier.id,
  label: supplier.name,
  secondary: supplier.email ?? supplier.phone,
}));

const networks = createListCollection({
  items: Object.entries(SUPPLIER_PAYOUT_NETWORK_LABEL).map(([value, label]) => ({
    value,
    label,
  })),
});

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
  fontSize: 'sm',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Flex direction="column" gap={1}>
    <Text fontSize="xs" color="text.secondary">
      {label}
    </Text>
    {children}
  </Flex>
);

interface Props {
  supplierId: string;
  onSelectSupplier: (id: string) => void;
  onCreateSupplier?: (query: string) => void;
  amount: string;
  onAmountChange: (value: string) => void;
  // Optional decoration on the amount field, e.g. "/ 12.50 USDC" against the link target.
  amountEndLabel?: string;
  network: string;
  onNetworkChange: (value: string) => void;
  address: string;
  onAddressChange: (value: string) => void;
  memo: string;
  onMemoChange: (value: string) => void;
}

// The shared "pay supplier" inputs — used both in the simplified payment builder
// and inside the Supplier Payout node's config on the routing canvas.
const PaySupplierForm = memo(
  ({
    supplierId,
    onSelectSupplier,
    onCreateSupplier,
    amount,
    onAmountChange,
    amountEndLabel,
    network,
    onNetworkChange,
    address,
    onAddressChange,
    memo: memoValue,
    onMemoChange,
  }: Props) => {
    const endLabel = amountEndLabel ?? 'USDC';
    const onAmount = (e: React.ChangeEvent<HTMLInputElement>) => onAmountChange(e.target.value);
    const onNetwork = (details: { value: string[] }) => onNetworkChange(details.value[0] ?? '');
    const onAddress = (e: React.ChangeEvent<HTMLInputElement>) => onAddressChange(e.target.value);
    const onMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => onMemoChange(e.target.value);
    const longLabel = useMemo(() => endLabel.length > 6, [endLabel]);

    return (
      <Flex direction="column" gap={4}>
        <Field label="Supplier">
          <EntityPicker
            value={supplierId}
            options={supplierOptions}
            placeholder="Who gets paid"
            createLabel="supplier"
            onSelect={onSelectSupplier}
            onCreate={onCreateSupplier}
          />
        </Field>
        <SimpleGrid columns={2} gap={4}>
          <Field label="Amount to send">
            <InputGroup
              endElement={
                <Text fontSize="sm" color="text.secondary" whiteSpace="nowrap">
                  {endLabel}
                </Text>
              }
              endElementProps={{ pointerEvents: 'none' }}
            >
              <Input
                placeholder="0.00"
                type="number"
                value={amount}
                onChange={onAmount}
                pe={longLabel ? '8.5rem' : '3.5rem'}
                {...inputProps}
              />
            </InputGroup>
          </Field>
          <Field label="Network">
            <SelectField
              collection={networks}
              placeholder="Select network"
              value={network !== '' ? [network] : []}
              onValueChange={onNetwork}
            />
          </Field>
        </SimpleGrid>
        <Field label="Supplier wallet address">
          <Input placeholder="0x… or G…" value={address} onChange={onAddress} {...inputProps} />
        </Field>
        <Field label="Memo / tag (optional)">
          <Textarea
            placeholder="Destination tag, if the network needs one"
            rows={2}
            value={memoValue}
            onChange={onMemo}
            {...inputProps}
          />
        </Field>
      </Flex>
    );
  },
);
PaySupplierForm.displayName = 'PaySupplierForm';

export default PaySupplierForm;
